import { useTranslate } from 'core/hooks/useTranslate';
import { useCallback, useEffect, useState } from 'react';

type UseAddModuleDialogProps = {
  type: 'module' | 'submodule';
  onConfirm: (name: string) => Promise<boolean>;
  onClose: () => void;
  isLoading?: boolean;
  initialName?: string;
  mode?: 'add' | 'edit';
};

type UseAddModuleDialogReturn = {
  t: (key: string) => string;
  title: string;
  label: string;
  disclaimer: string;
  name: string;
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleConfirm: () => Promise<void>;
  handleClose: () => void;
  isDisabled: boolean;
  showError: boolean;
  errorMessage: string;
};

function useAddModuleDialog({
  type,
  onClose,
  onConfirm,
  isLoading = false,
  initialName = '',
  mode = 'add',
}: UseAddModuleDialogProps): UseAddModuleDialogReturn {
  const { t } = useTranslate();
  const [name, setName] = useState(initialName);

  // Actualizar el nombre cuando cambie initialName
  useEffect(() => {
    if (initialName !== undefined) {
      setName(initialName);
    }
  }, [initialName]);

  const isModule = type === 'module';

  // Título dinámico según el modo
  const title =
    mode === 'edit'
      ? isModule
        ? t('organize-content:Edit module')
        : t('organize-content:Edit submodule')
      : isModule
      ? t('organize-content:New module')
      : t('organize-content:New submodule');

  // Label dinámico según el modo
  const label =
    mode === 'edit'
      ? isModule
        ? t('organize-content:Edit module name')
        : t('organize-content:Edit submodule name')
      : isModule
      ? t('organize-content:Module name')
      : t('organize-content:Submodule name');

  const disclaimer =
    mode === 'add'
      ? isModule
        ? t('organize-content:New module hidden disclaimer')
        : t('organize-content:New submodule hidden disclaimer')
      : t('organize-content:The change will apply to all classes');

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (name.trim()) {
      const isSuccess = await onConfirm(name.trim());
      // On failure the dialog stays open, so the typed name must survive for the retry
      if (isSuccess) {
        setName(mode === 'add' ? '' : initialName);
      }
    }
  }, [name, onConfirm, mode, initialName]);

  const handleClose = useCallback(() => {
    setName(mode === 'add' ? '' : initialName);
    onClose();
  }, [onClose, mode, initialName]);

  const MIN_NAME_LENGTH = 2;
  const trimmedName = name.trim();
  const isNameTooShort = trimmedName.length > 0 && trimmedName.length < MIN_NAME_LENGTH;
  const isDisabled = trimmedName.length < MIN_NAME_LENGTH || isLoading;

  return {
    t,
    title,
    label,
    disclaimer,
    name,
    handleNameChange,
    handleConfirm,
    handleClose,
    isDisabled,
    showError: isNameTooShort,
    errorMessage: t('organize-content:Name must be at least 2 characters'),
  };
}

export default useAddModuleDialog;
